import React, {useEffect, useState} from "react";
import {LoggedInView} from "../common/LoggedInView";
import {Link, useNavigate, useParams} from "react-router-dom";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogInput from "../common/form/ElogInput";
import ElogSelect, {Option} from "../common/form/ElogSelect";
import ElogCheckbox from "../common/form/ElogCheckbox";
import {createClientContact, resetCreateClientContactStatus} from "../../store/reducers/ClientContactsSlice";
import {RequestStatus} from "../../utils/Enums";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {getCurrentClient, resetCreateClientStatus, updateClient} from "../../store/reducers/ClientsSlice";
import ElogTextarea from "../common/form/ElogTextarea";
import CategoryType from "../../models/CategoryType";
import {getCountries} from "../../store/reducers/CountriesSlice";
import {apiClient} from "../../utils/ApiClient";
import {FormikProvider, useFormik, Field, Form} from "formik";

interface CreateClientContactScreenProps {
}

export interface CreateClientContactFormProps {
    name: string;
    plea: string;
    email: string;
    phone: string;
    comment?: string;
    comment2?: string;
    no_feedback: boolean;
    language: string;
    banned: boolean;
    groups: string[];
}

export const NewClientContactScreen: React.FC<CreateClientContactScreenProps> = () => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {clientId} = useParams<{ clientId: string }>();

    const clientContactsState = useAppSelector((state) => state.clientContacts);
    const clientsState = useAppSelector((state) => state.clients);

    const requestStatus: RequestStatus = clientContactsState.createClientContactStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: "Kliendid", url: "/clients"},
        {text: "Kliendikontaktid", url: `/clients/${clientId}/contacts`},
        {text: "Lisa uus kliendikontakt", url: `/clients/${clientId}/contacts/create`},
    ];

    const [categoryTypes, setCategoryTypes] = useState<CategoryType[]>([]);

    const languageOptions: Option[] = [
        {value: 'est', label: 'Eesti'},
        {value: 'eng', label: 'Inglise'},
        {value: 'fin', label: 'Soome'}
    ];

    useEffect(() => {
        if (requestStatus === RequestStatus.Success) {
            dispatch(resetCreateClientContactStatus());
            navigate(breadcrumbLinks[1].url);
        }
    }, [requestStatus]);

    useEffect(() => {

        if ( clientId ) {
            dispatch(getCurrentClient({ id: clientId }));
        }

        apiClient.get("category-types/all-by-client/" + clientId).then((response) => {
            setCategoryTypes(response.data);
        });

    }, []);

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            language: 'est',
            comment: clientsState?.currentClient?.contacts?.[0]?.comment ?? '',
            emails: ['']
        },
        onSubmit: (values: any) => {
            if (clientId) {
                dispatch(createClientContact({clientId:parseInt(clientId, 10), clientContactData: values}));
            }
        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik;

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            {requestStatus === RequestStatus.Failed && clientContactsState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {clientContactsState.errors.map((error, index) => (
                            <span key={index}>{error}<br /></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2>
                            <span>Kliendikontakti andmed</span>
                        </h2>
                        <table className="form">
                            <tbody>
                            <ElogInput label="Nimi" name="name" mandatory={true}/>
                            <ElogInput label="Pöördumine" name="plea" mandatory={true}/>
                            {values.emails.map((email, index) => (
                                <>
                                    <ElogInput
                                        label={`E-mail ${index + 1}`}
                                        name={`emails.${index}`}
                                        type="email"
                                        mandatory={true}
                                        button={index > 0 && (
                                            <button
                                                style={{color: 'red'}}
                                                type="button"
                                                onClick={() => setFieldValue('emails', values.emails.filter((_, i) => i !== index))}
                                            >
                                                [X]
                                            </button>
                                        )}
                                    />
                                </>
                            ))}
                            <tr>
                                <th>
                                </th>
                                <td>
                                    <button
                                        type="button"
                                        onClick={() => setFieldValue('emails', [...values.emails, ''])}
                                    >
                                        Lisa
                                    </button>
                                </td>
                            </tr>
                            <ElogInput label="Telefon" name="phone" mandatory={true}/>
                            <ElogSelect label="Keel" name="language" options={languageOptions} mandatory={true} />
                            <ElogTextarea label="Taustainfo" name="comment"/>
                            <ElogCheckbox label={'Eemalda D14 listist'} name={'noD'}/>
                            <ElogCheckbox label="Eemalda mailikampaaniatest" name="banned"/>
                            </tbody>
                        </table>
                    </div>

                    {categoryTypes.map((categoryType) => (
                        <div key={categoryType.id} className="form">
                            <h2>
                                <span>{categoryType.name}</span>
                            </h2>
                            {categoryType?.categories?.map((category) => (
                                <Field
                                    key={category.id}
                                    name="groups"
                                    type="checkbox"
                                    value={String(category.id)}
                                    render={(props: { field: { value: any; onChange: (e: React.ChangeEvent<any>) => void; onBlur: () => void; name: string } }) => (
                                        <>
                                            <label htmlFor={`group_${category.id}`}>{category.name}</label>
                                            <input
                                                {...props.field}
                                                id={`group_${category.id}`}
                                                type="checkbox"
                                                onChange={(event) => props.field.onChange(event)}
                                                onBlur={() => undefined}
                                            />
                                        </>
                                    )}
                                />
                            ))}
                        </div>
                    ))}

                    <div className="action clear">
                        <p>
                            <button type="submit">
                                  <span>
                                    <strong>Loo</strong>
                                  </span>
                            </button>
                            <Link to={`/clients/${clientId}/contacts`}>Tühista</Link>
                        </p>
                    </div>

                </Form>
            </FormikProvider>
        </LoggedInView>
    );
};